import React from "react";
import append from "ramda/src/append";
import reject from "ramda/src/reject";
import propEq from "ramda/src/propEq";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { Modal } from "../Modal";
import { Button } from "../../UI/MUIButton";
import Api from "../../services/Api";
import { useNetwork } from "../../hooks/useNetwork";
import { EMAIL_ADDRESS, PHONE_NUMBER } from "../../constants/general";
import { Link } from "../Link";
import {
  useNetworkContext,
  useSelectedNetworkContext,
} from "../../contexts/NetworkContext/context";
import { SearchInput } from "../SearchInput";
import { IdNameTransport } from "../../services/Common";
import { useDebounce } from "../../hooks/useDebounce";
import { normalize } from "../../helpers/normalize";
import { SearchNetworkByName } from "../../services/NetworkApi";
import useAuth from "../../routes/login/AuthContext";
import { useModalContext } from "../../contexts/ModalContext/context";
import { useSnackbar } from "../../hooks/useSnackbar";
import { propId } from "../../helpers/propId";

interface IProps {
  l2Id: number;
}

interface ISelectedOption {
  name: string;
  id: number;
}

export const CopyGenderOptionsToOtherNetworksModal: React.FC<IProps> = (
  props
) => {
  const [selectedNetworks, setSelectedNetworks] = React.useState<
    ISelectedOption[]
  >([]);
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { getL2 } = useNetwork();
  const { getAuthToken } = useAuth();
  const { setIsOpen } = useModalContext();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();
  const { loadNetworkInfoForNetworks } = useNetworkContext();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isNetworksLoading, setIsNetworksLoading] = React.useState(false);
  const [networks, setNetworks] = React.useState<IdNameTransport[]>([]);
  const [searchValue, setSearchValue] = React.useState("");
  const debouncedValue = useDebounce(searchValue, 500);

  React.useEffect(() => {
    if (debouncedValue.length < 3) {
      setNetworks([]);
      return;
    }

    const getNetworks = async () => {
      setIsNetworksLoading(true);

      const results = await SearchNetworkByName(
        debouncedValue,
        await getAuthToken()
      );

      if (results) {
        setNetworks(results);
      }

      setIsNetworksLoading(false);
    };

    getNetworks();
  }, [normalize(debouncedValue)]);

  const currentSubnetwork = getL2(selectedNetworkId, props.l2Id);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.keyCode === 8) {
      setSearchValue("");
    }
  };

  const handleNetworkSelect = (id: number) => async () => {
    const selectedNetwork = networks.find(propEq("id", id));

    setSelectedNetworks(append(selectedNetwork) as any);
    setSearchValue("");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleFocus = () => {
    setSearchValue("");
  };

  const handleDeleteChip = (id: number) => () =>
    setSelectedNetworks(reject(propEq("id", id)));

  const handleUpdate = async () => {
    setIsLoading(true);

    const targets = selectedNetworks.map(({ id }) => ({
      l1Id: id,
    }));

    const res = await Api.copyGender(
      selectedNetworkId,
      {
        l1Id: selectedNetworkId,
        l2Id: props.l2Id,
        targets,
      },
      await getAuthToken()
    );

    if (res.success) {
      setIsOpen(false);
      showSuccessSnackbar("Successfully copied");
      loadNetworkInfoForNetworks(selectedNetworks.map(propId));
      setSelectedNetworks([]);
    } else {
      showErrorSnackbar("An Error Occurred");
    }

    setIsLoading(false);
  };

  return (
    <Modal
      maxWidth="sm"
      title="Apply Gender Options to Other Networks"
      actions={
        <Button
          color="primary"
          variant="outlined"
          loading={isLoading}
          onClick={handleUpdate}
        >
          Update Gender Options
        </Button>
      }
    >
      <Box minHeight={300} px={3} py={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography gutterBottom>
              The current gender options for{" "}
              <strong>{currentSubnetwork?.l2Name}</strong> will replace the
              gender options for the networks selected below. Please select the
              networks to overwrite for gender options:
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <SearchInput
              fullWidth
              label="Network"
              options={networks}
              value={searchValue}
              handleFocus={handleFocus}
              handleChange={handleChange}
              handleKeyDown={handleKeyDown}
              isLoading={isNetworksLoading}
              handleOptionSelect={handleNetworkSelect}
            />
          </Grid>
          <Grid item container spacing={1}>
            {selectedNetworks.map(({ id, name }) => (
              <Grid item key={id}>
                <Chip
                  color="primary"
                  label={`${name} - ${id}`}
                  onDelete={handleDeleteChip(id)}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12}>
            <Typography gutterBottom>
              If you have any questions, please contact us at{" "}
              <Link href={`mailto:${EMAIL_ADDRESS}`}>{EMAIL_ADDRESS}</Link> or
              call {PHONE_NUMBER}.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};
